import * as maplibregl from 'maplibre-gl'
import type { MapColors } from '@/lib/mapStyle'
import { LAYERS, SOURCES } from './visitorMapLayers'

interface SetupOptions {
  map: maplibregl.Map
  colors: MapColors
  onVisitorClick?: (visitorId: string) => void
  formatLastSeen: (isoDate: string | null) => string
}

/** Sets up marker click handlers, popups, and cursor changes */
export function setupMarkerInteractions({ map, colors, onVisitorClick, formatLastSeen }: SetupOptions) {
  // Popup for visitor markers
  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: true,
    offset: 12,
    className: 'visitor-popup',
  })

  // Handler for individual marker clicks (works for both live and active)
  const handleMarkerClick = (e: maplibregl.MapLayerMouseEvent) => {
    const feature = e.features?.[0]
    if (!feature) return

    const props = feature.properties
    const id = props?.id
    const geometry = feature.geometry as GeoJSON.Point

    if (id && onVisitorClick) {
      onVisitorClick(id as string)
    }

    // Show popup
    const location = [props?.city, props?.country_code].filter(Boolean).join(', ') || 'Unknown location'
    const isLive = props?.live
    const isActive = props?.active
    const statusDot = isLive
      ? `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${colors.ghost};margin-right:6px;animation:popup-pulse 2s infinite;"></span>`
      : isActive
        ? `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${colors.ghost};margin-right:6px;"></span>`
        : `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${colors.ghostDark};opacity:0.6;margin-right:6px;"></span>`
    const statusText = isLive ? 'Live now' : isActive ? 'Active' : formatLastSeen(props?.last_seen_at)

    popup
      .setLngLat(geometry.coordinates as [number, number])
      .setHTML(`
        <div style="font-family:system-ui,sans-serif;font-size:13px;color:${colors.popupText};min-width:140px;">
          <div style="font-weight:600;margin-bottom:4px;">${props?.name || 'Visitor'}</div>
          <div style="color:${colors.popupMuted};font-size:12px;margin-bottom:4px;">${location}</div>
          <div style="display:flex;align-items:center;font-size:12px;color:${colors.popupMuted};">
            ${statusDot}${statusText}
          </div>
        </div>
      `)
      .addTo(map)
  }

  // Click handlers for individual markers
  map.on('click', LAYERS.LIVE_MARKERS, handleMarkerClick)
  map.on('click', LAYERS.ACTIVE_MARKERS, handleMarkerClick)

  // Handler for cluster clicks - zoom in to expand
  const createClusterClickHandler = (sourceId: string, layerId: string) => {
    return async (e: maplibregl.MapLayerMouseEvent) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [layerId] })
      const clusterId = features[0]?.properties?.cluster_id
      const source = map.getSource(sourceId) as maplibregl.GeoJSONSource

      try {
        const zoom = await source.getClusterExpansionZoom(clusterId)
        const geometry = features[0].geometry as GeoJSON.Point
        map.easeTo({
          center: geometry.coordinates as [number, number],
          zoom: zoom ?? 10,
        })
      } catch {
        // Cluster might have been removed
      }
    }
  }

  // Click handlers for clusters
  map.on('click', LAYERS.LIVE_CLUSTERS, createClusterClickHandler(SOURCES.LIVE, LAYERS.LIVE_CLUSTERS))
  map.on('click', LAYERS.ACTIVE_CLUSTERS, createClusterClickHandler(SOURCES.ACTIVE, LAYERS.ACTIVE_CLUSTERS))

  // Cursor changes on hover for markers
  const pointerLayers = [
    LAYERS.LIVE_MARKERS,
    LAYERS.ACTIVE_MARKERS,
    LAYERS.LIVE_CLUSTERS,
    LAYERS.ACTIVE_CLUSTERS,
  ]

  for (const layer of pointerLayers) {
    map.on('mouseenter', layer, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', layer, () => {
      map.getCanvas().style.cursor = ''
    })
  }
}

/** Formats relative time from ISO date string */
export function formatLastSeen(isoDate: string | null): string {
  if (!isoDate) return 'Unknown'
  const date = new Date(isoDate)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}
